import type { DrawingDocument } from '../model';
import { COMMON_SUBSTITUENTS } from './markushSubstituents.ts';
import { importSmiles } from './smilesImport.ts';

/**
 * #736 step 1: the functional groups a chemist writes as text -- placed on the paper as their real
 * atoms and bonds. Each is SMILES with the atom that bonds to the rest **first** (the Markush
 * convention, #652); a group the Markush substituent table also has takes its SMILES from there.
 * `label` is the chip text, `name` its tooltip. A one-atom group (OH, NH₂, Me) isn't here: an
 * element from the periodic table on an end atom draws it already (owner, 2026-11-02).
 */
export interface FunctionalGroup {
  readonly key: string;
  readonly label: string;
  /** #736 step 2: the label when the group's bond comes from the right (F₃C–, HO₂C–). */
  readonly reversed: string;
  readonly name: string;
  readonly smiles: string;
}

const markush = (key: string) => {
  const substituent = COMMON_SUBSTITUENTS.find((entry) => entry.key === key);
  if (!substituent) throw new Error(`No Markush substituent ${key}.`);
  return substituent.smiles;
};

export const FUNCTIONAL_GROUPS: readonly FunctionalGroup[] = [
  { key: 'Et', label: 'Et', reversed: 'Et', name: 'Ethyl, C₂H₅', smiles: markush('C2H5') },
  { key: 'iPr', label: 'iPr', reversed: 'iPr', name: 'Isopropyl, CH(CH₃)₂', smiles: 'C(C)C' },
  { key: 'tBu', label: 'tBu', reversed: 'tBu', name: 'tert-Butyl, C(CH₃)₃', smiles: 'C(C)(C)C' },
  { key: 'Ph', label: 'Ph', reversed: 'Ph', name: 'Phenyl, C₆H₅', smiles: markush('Ph') },
  { key: 'Bn', label: 'Bn', reversed: 'Bn', name: 'Benzyl, CH₂C₆H₅', smiles: 'Cc1ccccc1' },
  { key: 'CF3', label: 'CF₃', reversed: 'F₃C', name: 'Trifluoromethyl', smiles: markush('CF3') },
  { key: 'OMe', label: 'OMe', reversed: 'MeO', name: 'Methoxy, OCH₃', smiles: markush('OCH3') },
  { key: 'NO2', label: 'NO₂', reversed: 'O₂N', name: 'Nitro', smiles: markush('NO2') },
  { key: 'CN', label: 'CN', reversed: 'NC', name: 'Cyano (nitrile)', smiles: markush('CN') },
  { key: 'CHO', label: 'CHO', reversed: 'OHC', name: 'Formyl (aldehyde)', smiles: 'C=O' },
  { key: 'COOH', label: 'CO₂H', reversed: 'HO₂C', name: 'Carboxy (carboxylic acid)', smiles: markush('COOH') },
  { key: 'CO2Me', label: 'CO₂Me', reversed: 'MeO₂C', name: 'Methoxycarbonyl (methyl ester)', smiles: 'C(=O)OC' },
  { key: 'Ac', label: 'Ac', reversed: 'Ac', name: 'Acetyl, COCH₃', smiles: 'C(C)=O' },
  { key: 'OAc', label: 'OAc', reversed: 'AcO', name: 'Acetoxy, OCOCH₃', smiles: 'OC(C)=O' },
  { key: 'SO3H', label: 'SO₃H', reversed: 'HO₃S', name: 'Sulfo (sulfonic acid)', smiles: 'S(=O)(=O)O' },
  { key: 'Boc', label: 'Boc', reversed: 'Boc', name: 'tert-Butoxycarbonyl', smiles: 'C(=O)OC(C)(C)C' }
];

/**
 * Laid out once by RDKit, then reused -- with a stand-in carbon bonded to the group's first atom
 * (`C` + its SMILES), so the layout has the bond the group attaches by at a proper angle (#736: laid
 * out alone, an ethyl went on straight, looking like one long bond). The stand-in is the document's
 * first atom, the group's own first atom its second.
 */
const PAPER = { width: 400, height: 400 };
const cache = new Map<string, Promise<DrawingDocument>>();

export function functionalGroupDocument(key: string): Promise<DrawingDocument> {
  const group = FUNCTIONAL_GROUPS.find((entry) => entry.key === key);
  if (!group) return Promise.reject(new Error(`Unknown group ${key}.`));
  let pending = cache.get(key);
  if (!pending) {
    pending = importSmiles(`C${group.smiles}`, PAPER).then((result) => result.document);
    pending.catch(() => cache.delete(key));
    cache.set(key, pending);
  }
  return pending;
}
